/**
 * @fileoverview Combobox (autocomplete) component with search and filtering
 * @module ctl-combobox
 * @version 1.0.0
 * 
 * @description
 * A combobox (autocomplete) component that combines a text input with a dropdown
 * list of suggestions. Users can type to filter options or select from the list.
 * Provides real-time filtering, keyboard navigation, and works seamlessly with
 * both predefined option lists and dynamic data sources.
 * 
 * The component is ideal for searchable dropdowns, autocomplete fields, and
 * any scenario where users need to find and select from a large list of options.
 * It supports custom filtering logic, option templates, and form integration.
 * 
 * @example
 * <!-- Basic combobox (use .items property to set options) -->
 * <ctl-combobox 
 *   id="country-combo"
 *   placeholder="Search countries...">
 * </ctl-combobox>
 * <script type="module">
 *   const combo = document.querySelector('#country-combo');
 *   combo.items = [
 *     { value: 'us', label: 'United States' },
 *     { value: 'ca', label: 'Canada' },
 *     { value: 'mx', label: 'Mexico' }
 *   ];
 * </script>
 * 
 * @example
 * <!-- Combobox with options (using .items property) -->
 * <ctl-combobox id="city-combo" placeholder="Select a city"></ctl-combobox>
 * <script type="module">
 *   const combo = document.querySelector('#city-combo');
 *   combo.items = [
 *     { value: 'nyc', label: 'New York' },
 *     { value: 'la', label: 'Los Angeles' },
 *     { value: 'chi', label: 'Chicago' }
 *   ];
 * </script>
 * 
 * @example
 * <!-- Disabled combobox -->
 * <ctl-combobox 
 *   placeholder="Unavailable" 
 *   disabled>
 * </ctl-combobox>
 * 
 * @example
 * <!-- Combobox with validation -->
 * <ctl-form-field label="Select City" required>
 *   <ctl-combobox placeholder="Type to search..."></ctl-combobox>
 * </ctl-form-field>
 */

import { dispatchEvent } from '../../../utils/events.js';

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-shadow-md: var(--ctl-shadow-md, 0 12px 34px rgba(0,0,0,.14));
    --ctl-radius-md: var(--ctl-radius-md, 12px);
    --ctl-radius-lg: var(--ctl-radius-lg, 14px);
    --ctl-focus-ring: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.18));
    --ctl-input-h: var(--ctl-input-height, 40px);

    display:inline-block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
    width: 100%;
    max-width: 520px;
  }

  .root{
    position: relative;
    width: 100%;
  }

  .control{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    height: var(--ctl-input-h);
    padding: 0 var(--ctl-input-padding-x);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    background: var(--ctl-color-surface);
    transition: border-color var(--ctl-transition-fast), box-shadow var(--ctl-transition-fast);
  }

  :host([disabled]) .control{
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
    background: var(--ctl-color-surface-2);
  }

  :host([open]) .control{
    border-color: color-mix(in srgb, var(--ctl-color-brand) 45%, var(--ctl-color-border));
  }

  .control:focus-within{
    border-color: color-mix(in srgb, var(--ctl-color-brand) 55%, var(--ctl-color-border));
    box-shadow: var(--ctl-focus-ring);
  }

  .prefix, .suffix{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    color: var(--ctl-color-muted);
  }

  .input{
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: var(--ctl-color-text);
    font-weight: var(--ctl-font-weight-semibold);
    font-size: var(--ctl-font-size-md);
    line-height: 1;
    font-family: var(--ctl-font);
  }

  .input::placeholder{
    color: color-mix(in srgb, var(--ctl-color-muted) 75%, transparent);
    font-weight: var(--ctl-font-weight-demi);
  }

  .btn{
    all: unset;
    cursor: pointer;
    width: var(--ctl-size-icon-btn);
    height: var(--ctl-size-icon-btn);
    border-radius: var(--ctl-radius-md);
    display:inline-flex;
    align-items:center;
    justify-content:center;
    color: var(--ctl-color-muted);
  }

  .btn:hover{
    background: color-mix(in srgb, var(--ctl-color-brand) 8%, transparent);
    color: var(--ctl-color-text);
  }

  :host([disabled]) .btn{
    cursor: not-allowed;
    pointer-events:none;
  }

  .panel{
    position: fixed;
    z-index: 2400;
    min-width: 220px;
    max-width: min(520px, calc(100vw - var(--spacing-md)));
    max-height: 320px;
    overflow:auto;
    background: var(--ctl-color-surface);
    border: var(--border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-md);
    padding: var(--spacing-xxs);
    opacity: var(--opacity-0);
    pointer-events: none;
    transform: translateY(calc(-1 * calc(var(--spacing-xxs) / 2)));
    transition: opacity 120ms ease, transform 120ms ease;
  }

  :host([open]) .panel{
    opacity: var(--opacity-100);
    pointer-events: auto;
    transform: translateY(var(--opacity-0));
  }

  .list{
    display:flex;
    flex-direction: column;
    gap: calc(var(--spacing-xxs) / 2);
  }

  .opt{
    all: unset;
    box-sizing: border-box;
    width: 100%;
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: var(--ctl-space-2);
    padding: var(--ctl-space-2);
    border-radius: var(--ctl-radius-md);
    cursor: pointer;
    color: var(--ctl-color-text);
  }

  .opt:hover{
    background: color-mix(in srgb, var(--ctl-color-brand) 7%, transparent);
  }

  .opt[aria-selected="true"]{
    background: color-mix(in srgb, var(--ctl-color-brand) 10%, transparent);
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--ctl-color-brand) 18%, var(--ctl-color-border));
  }

  .opt[data-active="true"]{
    background: color-mix(in srgb, var(--ctl-color-brand) 12%, transparent);
  }

  .opt[aria-disabled="true"]{
    opacity: .55;
    cursor: not-allowed;
  }
  .opt[aria-disabled="true"]:hover{
    background: transparent;
  }

  .label{
    min-width: 0;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: var(--ctl-font-weight-bold);
    font-size: var(--ctl-font-size-md);
  }

  .meta{
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-strong);
    white-space: nowrap;
  }

  .empty{
    padding: var(--ctl-space-2);
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-strong);
  }

  .sr-only{
    position:absolute;
    width:var(--border-width-thin);height:var(--border-width-thin);
    padding:0;margin:calc(-1 * var(--border-width-thin));
    overflow:hidden;clip:rect(0,0,0,0);
    border:0;
  }
</style>

<div class="root">
  <div class="control" id="control">
    <span class="prefix"><slot name="prefix"></slot></span>

    <input
      id="input"
      class="input"
      type="text"
      autocomplete="off"
      autocapitalize="none"
      spellcheck="false"
    />

    <button class="btn" id="clearBtn" type="button" aria-label="Clear" title="Clear" hidden>✕</button>
    <button class="btn" id="toggleBtn" type="button" aria-label="Toggle" title="Toggle" aria-hidden="true">▾</button>

    <span class="suffix"><slot name="suffix"></slot></span>
  </div>

  <div
    class="panel"
    id="panel"
    role="listbox"
    aria-label="Options"
  >
    <div class="list" id="list"></div>
    <div class="empty" id="empty" hidden>No results</div>
  </div>

  <span class="sr-only" aria-live="polite" id="live"></span>
</div>
`;

function clamp(n, a, b){ return Math.max(a, Math.min(b, n)); }
function uid(prefix="ctl"){ return `${prefix}-${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`; }

/**
 * Combobox (Autocomplete) Web Component
 * 
 * A searchable dropdown that combines text input with filtered option selection.
 * Provides autocomplete functionality with keyboard navigation and custom filtering.
 * 
 * @class CtlCombobox
 * @extends HTMLElement
 * 
 * @attr {string} value - Currently selected value
 * @attr {string} placeholder - Placeholder text for the input
 * @attr {boolean} disabled - Whether combobox is disabled
 * @attr {boolean} readonly - Whether input is read-only
 * @attr {boolean} clearable - Shows clear button when value is selected
 * @attr {boolean} open - Controls dropdown open state
 * @attr {boolean} allow-custom - Allows custom values not in the options list
 * 
 * @slot prefix - Content rendered before the input (e.g., icon or label)
 * @slot suffix - Content rendered after the input (e.g., icon or button)
 * 
 * @fires ctl-change - Dispatched when selected value changes (detail.value and detail.label)
 * @fires ctl-input - Dispatched when input value changes (search query with detail.value)
 * @fires ctl-open-change - Dispatched when dropdown open state changes (detail.open and detail.reason)
 * 
 * @cssprop --ctl-font - Font family
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted/placeholder text color
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-surface-2 - Secondary background color
 * @cssprop --ctl-color-brand - Brand/focus/highlight color
 * @cssprop --ctl-shadow-md - Dropdown shadow (default: 0 12px 34px rgba(0,0,0,.14))
 * @cssprop --ctl-radius-md - Medium border radius (default: 12px)
 * @cssprop --ctl-radius-lg - Large border radius (default: 14px)
 * @cssprop --ctl-focus-ring - Focus ring shadow (default: 0 0 0 3px rgba(79,70,229,.18))
 * @cssprop --ctl-input-height - Input height (default: 40px)
 */
export class CtlCombobox extends HTMLElement {
  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes(){
    return ["value", "placeholder", "disabled", "readonly", "clearable", "open", "allow-custom"];
  }

  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._idBase = uid("ctl-combobox");
    this._control = this.shadowRoot.getElementById("control");
    this._input = this.shadowRoot.getElementById("input");
    this._panel = this.shadowRoot.getElementById("panel");
    this._list = this.shadowRoot.getElementById("list");
    this._empty = this.shadowRoot.getElementById("empty");
    this._live = this.shadowRoot.getElementById("live");
    this._clearBtn = this.shadowRoot.getElementById("clearBtn");
    this._toggleBtn = this.shadowRoot.getElementById("toggleBtn");

    this._items = [];
    this._filtered = [];
    this._activeIndex = -1;
    this._selected = null; // {value,label,...}
    this._query = "";
    this._hasDocumentListener = false; // Track document listener state

    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onToggle = this._onToggle.bind(this);
    this._onClear = this._onClear.bind(this);
    
    // Event delegation handlers for options (to prevent memory leaks)
    this._onOptionHover = this._onOptionHover.bind(this);
    this._onOptionMouseDown = this._onOptionMouseDown.bind(this);
    this._onOptionClick = this._onOptionClick.bind(this);
  }

  connectedCallback(){
    // ARIA wiring
    this._input.setAttribute("role", "combobox");
    this._input.setAttribute("aria-autocomplete", "list");
    this._input.setAttribute("aria-expanded", this.open ? "true" : "false");

    const listId = `${this._idBase}-listbox`;
    this._panel.id = listId;
    this._input.setAttribute("aria-controls", listId);

    // events
    this._input.addEventListener("keydown", this._onKeyDown);
    this._input.addEventListener("input", this._onInput);
    this._input.addEventListener("focus", this._onFocus);
    this._input.addEventListener("blur", this._onBlur);

    this._toggleBtn.addEventListener("click", this._onToggle);
    this._clearBtn.addEventListener("click", this._onClear);
    
    // Event delegation for options (prevents memory leaks)
    this._list.addEventListener("mouseenter", this._onOptionHover, true);
    this._list.addEventListener("mousedown", this._onOptionMouseDown);
    this._list.addEventListener("click", this._onOptionClick);

    this._syncFromAttrs();
    this._filterAndRender();
  }

  disconnectedCallback(){
    // Force hide to ensure document listener cleanup
    if (this.open) {
      this.hide("disconnect");
    }
    
    // Additional safety check
    if (this._hasDocumentListener) {
      document.removeEventListener("pointerdown", this._onDocPointerDown, true);
      this._hasDocumentListener = false;
    }
    
    this._input.removeEventListener("keydown", this._onKeyDown);
    this._input.removeEventListener("input", this._onInput);
    this._input.removeEventListener("focus", this._onFocus);
    this._input.removeEventListener("blur", this._onBlur);
    this._toggleBtn.removeEventListener("click", this._onToggle);
    this._clearBtn.removeEventListener("click", this._onClear);
    
    // Remove event delegation listeners
    this._list.removeEventListener("mouseenter", this._onOptionHover, true);
    this._list.removeEventListener("mousedown", this._onOptionMouseDown);
    this._list.removeEventListener("click", this._onOptionClick);
  }

  attributeChangedCallback(){
    this._syncFromAttrs();
    this._filterAndRender();
  }

  // ---------- Public API ----------
  get items(){ return this._items; }
  set items(v){
    this._items = Array.isArray(v) ? v.slice() : [];
    // if we already have a value, re-resolve selection from items
    this._resolveSelectionFromValue();
    this._filterAndRender();
  }

  get value(){ return this.getAttribute("value") || ""; }
  set value(v){
    const nv = String(v ?? "");
    this.setAttribute("value", nv);
    this._resolveSelectionFromValue();
    this._filterAndRender();
  }

  // display label (input text when a value is selected)
  get label(){ return this._selected?.label || ""; }

  get open(){ return this.hasAttribute("open"); }
  set open(v){ v ? this.setAttribute("open","") : this.removeAttribute("open"); }

  get allowCustom(){ return this.hasAttribute("allow-custom"); }
  set allowCustom(v){ v ? this.setAttribute("allow-custom","") : this.removeAttribute("allow-custom"); }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  get readonly(){ return this.hasAttribute("readonly"); }
  set readonly(v){ v ? this.setAttribute("readonly","") : this.removeAttribute("readonly"); }

  get clearable(){ return this.hasAttribute("clearable"); }
  set clearable(v){ v ? this.setAttribute("clearable","") : this.removeAttribute("clearable"); }

  focus(){ this._input.focus(); }

  show(reason="program"){
    if (this.disabled) return;
    if (this.open) return;
    this.open = true;
    this._positionPanel();
    
    // Guard against multiple document listeners
    if (!this._hasDocumentListener) {
      document.addEventListener("pointerdown", this._onDocPointerDown, true);
      this._hasDocumentListener = true;
    }
    
    this._input.setAttribute("aria-expanded", "true");
    dispatchEvent(this, "ctl-open-change", { open:true, reason });
  }

  hide(reason="dismiss"){
    if (!this.open) return;
    this.open = false;
    
    // Remove document listener
    if (this._hasDocumentListener) {
      document.removeEventListener("pointerdown", this._onDocPointerDown, true);
      this._hasDocumentListener = false;
    }
    
    this._input.setAttribute("aria-expanded", "false");
    this._activeIndex = -1;
    this._updateActivedescendant();
    dispatchEvent(this, "ctl-open-change", { open:false, reason });
  }

  clear({ emit = true } = {}){
    this._selected = null;
    this._query = "";
    this.removeAttribute("value");
    this._input.value = "";
    this._activeIndex = -1;
    this._filterAndRender();

    if (emit) {
      dispatchEvent(this, "ctl-change", { value: "", item: null, label: "", custom: false });
    }
  }

  // ---------- Internal ----------
  _syncFromAttrs(){
    this._input.placeholder = this.getAttribute("placeholder") || "";

    const dis = this.disabled;
    this._input.disabled = dis;
    this._toggleBtn.toggleAttribute("disabled", dis);

    const ro = this.readonly;
    this._input.readOnly = ro;

    // clear button
    const showClear = this.clearable && !dis && !ro && (this._input.value || this.value);
    this._clearBtn.hidden = !showClear;

    // keep selected label in input when not actively typing (unless user typed)
    // do not override while focused & typing unless selection exists.
    this._resolveSelectionFromValue();
  }

  _resolveSelectionFromValue(){
    const v = this.value;
    if (!v) {
      if (!this._input.matches(":focus")) this._input.value = "";
      this._selected = null;
      return;
    }
    const found = (this._items || []).find(it => String(it.value) === String(v));
    if (found) {
      this._selected = { ...found, value: String(found.value), label: String(found.label ?? found.value) };
      // If not typing, display selected label
      if (!this._input.matches(":focus") || this._input.value === "" || this._input.value === this._query) {
        this._input.value = this._selected.label;
      }
    }
  }

  _onDocPointerDown(e){
    if (!this.open) return;
    const path = e.composedPath ? e.composedPath() : [];
    const inside = path.includes(this) || path.includes(this._panel);
    if (inside) return;
    this.hide("outside");
  }

  _onFocus(){
    if (this.disabled) return;
    // treat current input as query when focusing, unless it equals selected label
    const txt = (this._input.value || "").trim();
    this._query = txt;
    this.show("focus");
  }

  _onBlur(){
    // Delay to allow click selection inside panel
    setTimeout(() => {
      const active = document.activeElement;
      const stillWithin = active && (active === this || this.contains(active) || this.shadowRoot.contains(active));
      if (!stillWithin) this.hide("blur");
      // If user typed but didn't select, revert to selected label unless allow-custom
      if (!this.allowCustom && this._selected) {
        const typed = (this._input.value || "").trim();
        if (typed && typed !== this._selected.label) {
          this._input.value = this._selected.label;
        }
      }
      this._syncFromAttrs();
    }, 0);
  }

  _onToggle(){
    if (this.disabled || this.readonly) return;
    this.open ? this.hide("toggle") : this.show("toggle");
    this._input.focus();
  }

  _onClear(){
    if (this.disabled || this.readonly) return;
    this.clear({ emit: true });
    this.show("clear");
    this._input.focus();
  }

  _onInput(){
    if (this.disabled || this.readonly) return;

    this._query = this._input.value;
    this._selected = null; // typing cancels selection until user picks
    this._activeIndex = -1;
    this._filterAndRender();
    this.show("input");

    // clear button visibility
    this._syncFromAttrs();

    // notify for async
    dispatchEvent(this, "ctl-input", { query: this._query });
  }

  _onKeyDown(e){
    if (this.disabled) return;

    if (e.key === "Escape") {
      e.preventDefault();
      this.hide("escape");
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!this.open) this.show("arrow");
      this._moveActive(+1);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!this.open) this.show("arrow");
      this._moveActive(-1);
      return;
    }

    if (e.key === "Home") {
      if (!this.open) return;
      e.preventDefault();
      this._setActiveIndex(0);
      return;
    }

    if (e.key === "End") {
      if (!this.open) return;
      e.preventDefault();
      this._setActiveIndex(this._filtered.length - 1);
      return;
    }

    if (e.key === "Enter") {
      if (!this.open) return; // allow form submit
      e.preventDefault();

      const active = this._filtered[this._activeIndex];
      if (active && !active.disabled) {
        this._selectItem(active, { reason: "enter" });
        return;
      }

      // custom value
      const typed = (this._input.value || "").trim();
      if (this.allowCustom && typed) {
        this._selectCustom(typed);
      }
      return;
    }

    // Tab closes dropdown but allows tab navigation
    if (e.key === "Tab") {
      this.hide("tab");
      return;
    }
  }

  _selectCustom(label){
    const v = label;
    this._selected = { value: v, label };
    this.setAttribute("value", v);
    this._input.value = label;
    this.hide("custom");

    dispatchEvent(this, "ctl-change", { value: v, item: null, label, custom: true });
  }

  _selectItem(item, { reason="select" } = {}){
    this._selected = { ...item, value: String(item.value), label: String(item.label ?? item.value) };
    this.setAttribute("value", this._selected.value);
    this._input.value = this._selected.label;

    this.hide(reason);
    this._syncFromAttrs();

    dispatchEvent(this, "ctl-change", { value: this._selected.value, item: this._selected, label: this._selected.label, custom: false });
  }

  _filterAndRender(){
    const q = (this._query || "").trim().toLowerCase();
    const all = Array.isArray(this._items) ? this._items : [];

    // If query empty, show all items (typical combobox behavior)
    let out = all;

    if (q) {
      out = all.filter(it => {
        const label = String(it.label ?? it.value ?? "").toLowerCase();
        const val = String(it.value ?? "").toLowerCase();
        return label.includes(q) || val.includes(q);
      });
    }

    this._filtered = out.map(it => ({
      value: String(it.value),
      label: String(it.label ?? it.value),
      disabled: !!it.disabled,
      meta: it.meta
    }));

    this._renderList();
    this._syncFromAttrs();
    if (this.open) this._positionPanel();
  }

  _renderList(){
    const selectedValue = this.value;
    this._list.innerHTML = "";

    if (!this._filtered.length) {
      this._empty.hidden = false;
      this._live.textContent = "No results.";
      this._activeIndex = -1;
      this._updateActivedescendant();
      return;
    }

    this._empty.hidden = true;
    this._live.textContent = `${this._filtered.length} options.`;

    this._filtered.forEach((it, idx) => {
      const opt = document.createElement("button");
      opt.type = "button";
      opt.className = "opt";
      opt.id = `${this._idBase}-opt-${idx}`;
      opt.setAttribute("role", "option");
      opt.setAttribute("aria-selected", String(it.value === selectedValue));
      opt.setAttribute("aria-disabled", it.disabled ? "true" : "false");
      opt.dataset.index = String(idx);
      opt.innerHTML = `
        <span class="label">${escapeHtml(it.label)}</span>
        ${it.meta ? `<span class="meta">${escapeHtml(String(it.meta))}</span>` : ``}
      `;

      // Event listeners removed - now using event delegation on parent

      this._list.appendChild(opt);
    });

    // If nothing active, set active to first enabled when open
    if (this.open && this._activeIndex < 0) {
      const firstEnabled = this._filtered.findIndex(x => !x.disabled);
      this._setActiveIndex(firstEnabled >= 0 ? firstEnabled : -1);
    } else {
      this._paintActive();
    }
  }
  
  // Event delegation handlers for options
  _onOptionHover(e){
    const opt = e.target.closest('[role="option"]');
    if (!opt) return;
    
    const idx = parseInt(opt.dataset.index, 10);
    if (isNaN(idx)) return;
    
    const item = this._filtered[idx];
    if (item && !item.disabled) {
      this._setActiveIndex(idx);
    }
  }
  
  _onOptionMouseDown(e){
    const opt = e.target.closest('[role="option"]');
    if (!opt) return;
    
    // prevent input blur before click handling
    e.preventDefault();
  }
  
  _onOptionClick(e){
    const opt = e.target.closest('[role="option"]');
    if (!opt) return;
    
    const idx = parseInt(opt.dataset.index, 10);
    if (isNaN(idx)) return;
    
    const item = this._filtered[idx];
    if (item && !item.disabled) {
      this._selectItem(item, { reason: "click" });
    }
  }

  _moveActive(delta){
    if (!this._filtered.length) return;

    const n = this._filtered.length;
    let i = this._activeIndex;

    for (let step = 0; step < n; step++) {
      i = (i < 0 ? (delta > 0 ? 0 : n - 1) : (i + delta + n) % n);
      if (!this._filtered[i].disabled) {
        this._setActiveIndex(i);
        return;
      }
    }
  }

  _setActiveIndex(i){
    if (i < 0 || i >= this._filtered.length) {
      this._activeIndex = -1;
      this._updateActivedescendant();
      this._paintActive();
      return;
    }
    if (this._filtered[i].disabled) return;

    this._activeIndex = i;
    this._updateActivedescendant();
    this._paintActive();
    this._scrollActiveIntoView();
  }

  _updateActivedescendant(){
    const id = (this._activeIndex >= 0) ? `${this._idBase}-opt-${this._activeIndex}` : "";
    if (id) this._input.setAttribute("aria-activedescendant", id);
    else this._input.removeAttribute("aria-activedescendant");
  }

  _paintActive(){
    const kids = this._list.children;
    for (let k = 0; k < kids.length; k++) {
      kids[k].dataset.active = (k === this._activeIndex) ? "true" : "false";
    }
  }

  _scrollActiveIntoView(){
    const el = this.shadowRoot.getElementById(`${this._idBase}-opt-${this._activeIndex}`);
    if (!el) return;
    el.scrollIntoView({ block: "nearest" });
  }

  _positionPanel(){
    const rect = this._control.getBoundingClientRect();
    const panel = this._panel;

    // move offscreen for measurement
    panel.style.left = "-9999px";
    panel.style.top = "-9999px";

    const pr = panel.getBoundingClientRect();
    const pw = pr.width || rect.width || 240;
    const ph = pr.height || 200;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--spacing-xs')) || 8;
    const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--spacing-xxs')) || 6;

    // Default: below
    let left = rect.left;
    let top = rect.bottom + offset;

    // Flip if needed
    if (top + ph + margin > vh) {
      top = rect.top - ph - offset;
    }

    left = clamp(left, margin, vw - pw - margin);
    top = clamp(top, margin, vh - ph - margin);

    panel.style.minWidth = `${Math.max(220, Math.round(rect.width))}px`;
    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
  }
}

function escapeHtml(s){
  return String(s)
    .replaceAll("&","&amp;")
    .replaceAll("<","&lt;")
    .replaceAll(">","&gt;")
    .replaceAll('"',"&quot;")
    .replaceAll("'","&#039;");
}

customElements.define("ctl-combobox", CtlCombobox);
